import React, { useState } from 'react'
import { ShoppingCart, Unlock, Lock, Plus, Clock, User, Phone, ChevronDown, ChevronRight, Eye, EyeOff } from 'lucide-react'
import {
  useKasirOTSSession,
  useOpenKasirSession,
  useCloseKasirSession,
  useCreateOTSOrder,
  type ChekiForm,
  type ChekiOrder,
} from '@/hooks/use-cheki'
import { useAuthStore } from '@/stores/auth-store'
import { formatCurrency, formatDate } from '@/lib/utils'

// ── Helpers ───────────────────────────────────────────────────────────────────

const PAYMENT_OPTIONS = [
  { value: 'cash', label: 'Cash' },
  { value: 'qris', label: 'QRIS' },
  { value: 'transfer', label: 'Transfer' },
] as const

type PaymentMethod = 'cash' | 'qris' | 'transfer'

function getOTSPrice(form: ChekiForm, isGroup: boolean): number {
  if (isGroup && form.group_price_ots_idr) return form.group_price_ots_idr
  if (isGroup && form.group_price_idr) return form.group_price_idr
  return form.price_ots_idr ?? form.price_idr
}

function getMembersForForm(form: ChekiForm) {
  if (form.quota_type === 'per_member' && form.cheki_form_members?.length) {
    return form.cheki_form_members.map((m) => ({
      id: m.member_id,
      name: m.idol_members?.stage_name ?? m.idol_members?.name ?? 'Unknown',
      photo_url: m.idol_members?.photo_url ?? null,
    }))
  }
  if (form.events?.event_members?.length) {
    return form.events.event_members.map((m) => ({
      id: m.member_id,
      name: m.idol_members?.stage_name ?? m.idol_members?.name ?? 'Unknown',
      photo_url: m.idol_members?.photo_url ?? null,
    }))
  }
  return []
}

// ── Session Header ─────────────────────────────────────────────────────────────

function SessionHeader({
  status,
  onOpen,
  onClose,
  sessionId,
  formId,
  isPending,
}: {
  status: 'open' | 'closed' | null
  onOpen: () => void
  onClose: (sessionId: string, formId: string) => void
  sessionId: string | null
  formId: string
  isPending: boolean
}) {
  const isOpen = status === 'open'

  return (
    <div className="flex items-center justify-between rounded-xl border border-border bg-card p-5">
      <div className="flex items-center gap-3">
        <div className={`h-3 w-3 rounded-full ${isOpen ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
        <div>
          <p className="text-sm font-semibold">
            {isOpen ? 'Sesi Kasir OTS Terbuka' : 'Sesi Kasir OTS Tertutup'}
          </p>
          <p className="text-xs text-muted-foreground">
            {isOpen
              ? 'Transaksi OTS dapat dicatat sekarang'
              : 'Buka sesi untuk mulai mencatat transaksi OTS'}
          </p>
        </div>
      </div>

      {isOpen && sessionId ? (
        <button
          onClick={() => onClose(sessionId, formId)}
          disabled={isPending}
          className="flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-accent disabled:opacity-50 transition-colors"
        >
          <Lock className="h-4 w-4" />
          Tutup Sesi
        </button>
      ) : (
        <button
          onClick={onOpen}
          disabled={isPending}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          <Unlock className="h-4 w-4" />
          Buka Sesi
        </button>
      )}
    </div>
  )
}

// ── Cart Item Type ─────────────────────────────────────────────────────────────

interface CartItem {
  localId: string
  memberId: string | ''
  qty: number
  isGroup: boolean
}

// ── Transaction Form ───────────────────────────────────────────────────────────

const GENERIC_MEMBER_KEY = '__generic__'

function TransactionForm({
  form,
  sessionId,
  hasGroupCheki,
}: {
  form: ChekiForm
  sessionId: string
  hasGroupCheki: boolean
}) {
  const tenantId = useAuthStore((s) => s.currentTenant?.id)
  const createOrder = useCreateOTSOrder()
  const members = getMembersForForm(form)

  const [fanName, setFanName] = useState('')
  const [fanPhone, setFanPhone] = useState('')
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cash')
  const [success, setSuccess] = useState(false)
  const [memberQty, setMemberQty] = useState<Record<string, number>>({})
  const [groupQty, setGroupQty] = useState(0)

  const canGroupCheki =
    hasGroupCheki && (form.group_price_ots_idr != null || form.group_price_idr != null)

  // Derive cart from qty state
  const cartItems: CartItem[] = [
    ...(members.length > 0
      ? members
          .filter((m) => (memberQty[m.id] ?? 0) > 0)
          .map((m) => ({ localId: m.id, memberId: m.id, qty: memberQty[m.id]!, isGroup: false }))
      : (memberQty[GENERIC_MEMBER_KEY] ?? 0) > 0
        ? [{ localId: GENERIC_MEMBER_KEY, memberId: '', qty: memberQty[GENERIC_MEMBER_KEY]!, isGroup: false }]
        : []),
    ...(canGroupCheki && groupQty > 0
      ? [{ localId: 'group', memberId: '', qty: groupQty, isGroup: true }]
      : []),
  ]

  const cartTotal = cartItems.reduce((sum, item) => {
    const effectiveIsGroup = item.isGroup && canGroupCheki
    return sum + getOTSPrice(form, effectiveIsGroup) * item.qty
  }, 0)
  const cartQty = cartItems.reduce((sum, item) => sum + item.qty, 0)

  function setCount(key: string, val: number) {
    setMemberQty((prev) => ({ ...prev, [key]: Math.max(0, val) }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!tenantId || cartItems.length === 0) return

    const transactionGroupId = crypto.randomUUID()

    for (const item of cartItems) {
      const effectiveIsGroup = item.isGroup && canGroupCheki
      const unitPrice = getOTSPrice(form, effectiveIsGroup)
      await createOrder.mutateAsync({
        form_id: form.id,
        tenant_id: tenantId,
        ots_session_id: sessionId,
        fan_name: fanName.trim() || undefined,
        fan_phone: fanPhone.trim() || undefined,
        member_id: item.memberId || null,
        quantity: item.qty,
        payment_method: paymentMethod,
        is_group_cheki: effectiveIsGroup,
        price_ots_idr: unitPrice,
        custom_data: { transaction_group_id: transactionGroupId },
      })
    }

    setFanName('')
    setFanPhone('')
    setMemberQty({})
    setGroupQty(0)
    setSuccess(true)
    setTimeout(() => setSuccess(false), 2000)
  }

  const submitLabel =
    success
      ? 'Tercatat!'
      : createOrder.isPending
        ? 'Menyimpan...'
        : cartItems.length > 1
          ? `Catat Semua (${cartItems.length} item)`
          : 'Catat Transaksi'

  const stepBtn = (disabled: boolean) =>
    `h-7 w-7 rounded border flex items-center justify-center text-sm font-medium transition-colors ${
      disabled
        ? 'border-border/40 text-muted-foreground/30 cursor-not-allowed'
        : 'border-border hover:bg-accent'
    }`

  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-4">
      <h3 className="font-semibold flex items-center gap-2 text-sm">
        <Plus className="h-4 w-4" />
        Catat Transaksi OTS
      </h3>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Nama & HP */}
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground flex items-center gap-1.5">
              <User className="h-3 w-3" />
              Nama Pembeli
              <span className="opacity-50">(opsional)</span>
            </label>
            <input
              type="text"
              value={fanName}
              onChange={(e) => setFanName(e.target.value)}
              placeholder="Nama fan..."
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground flex items-center gap-1.5">
              <Phone className="h-3 w-3" />
              No. HP
              <span className="opacity-50">(opsional)</span>
            </label>
            <input
              type="tel"
              value={fanPhone}
              onChange={(e) => setFanPhone(e.target.value)}
              placeholder="08xx..."
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>

        {/* Metode Bayar */}
        <div className="space-y-1.5">
          <label className="text-xs text-muted-foreground">Metode Bayar</label>
          <div className="flex gap-2">
            {PAYMENT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setPaymentMethod(opt.value)}
                className={`flex-1 rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${
                  paymentMethod === opt.value
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-background hover:bg-accent'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Member + Group Cheki Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {members.length > 0
            ? members.map((m) => {
                const qty = memberQty[m.id] ?? 0
                return (
                  <div
                    key={m.id}
                    className={`rounded-lg border p-3 transition-colors ${
                      qty > 0 ? 'border-primary bg-primary/5' : 'border-border bg-background'
                    }`}
                  >
                    <p className="text-sm font-medium truncate leading-tight">{m.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 mb-2.5">
                      {formatCurrency(form.price_ots_idr ?? form.price_idr)}
                    </p>
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => setCount(m.id, qty - 1)} disabled={qty === 0} className={stepBtn(qty === 0)}>−</button>
                      <span className="flex-1 text-center text-sm font-semibold tabular-nums">{qty}</span>
                      <button type="button" onClick={() => setCount(m.id, qty + 1)} className={stepBtn(false)}>+</button>
                    </div>
                  </div>
                )
              })
            : (() => {
                const qty = memberQty[GENERIC_MEMBER_KEY] ?? 0
                return (
                  <div className={`rounded-lg border p-3 transition-colors ${qty > 0 ? 'border-primary bg-primary/5' : 'border-border bg-background'}`}>
                    <p className="text-sm font-medium leading-tight">Cheki</p>
                    <p className="text-xs text-muted-foreground mt-0.5 mb-2.5">
                      {formatCurrency(form.price_ots_idr ?? form.price_idr)}
                    </p>
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => setCount(GENERIC_MEMBER_KEY, qty - 1)} disabled={qty === 0} className={stepBtn(qty === 0)}>−</button>
                      <span className="flex-1 text-center text-sm font-semibold tabular-nums">{qty}</span>
                      <button type="button" onClick={() => setCount(GENERIC_MEMBER_KEY, qty + 1)} className={stepBtn(false)}>+</button>
                    </div>
                  </div>
                )
              })()
          }
          {canGroupCheki && (
            <div className={`rounded-lg border p-3 transition-colors ${groupQty > 0 ? 'border-purple-400 bg-purple-50 dark:bg-purple-950/20' : 'border-border bg-background'}`}>
              <p className="text-sm font-medium leading-tight">Group Cheki</p>
              <p className="text-xs text-muted-foreground mt-0.5 mb-2.5">
                {formatCurrency(form.group_price_ots_idr ?? form.group_price_idr ?? form.price_idr)}
              </p>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setGroupQty((v) => Math.max(0, v - 1))} disabled={groupQty === 0} className={stepBtn(groupQty === 0)}>−</button>
                <span className="flex-1 text-center text-sm font-semibold tabular-nums">{groupQty}</span>
                <button type="button" onClick={() => setGroupQty((v) => v + 1)} className={stepBtn(false)}>+</button>
              </div>
            </div>
          )}
        </div>

        {/* Cart detail */}
        {cartItems.length > 0 && (
          <div className="rounded-lg border border-border overflow-hidden">
            <div className="divide-y divide-border">
              {cartItems.map((item) => {
                const effectiveIsGroup = item.isGroup && canGroupCheki
                const unitPrice = getOTSPrice(form, effectiveIsGroup)
                const label = effectiveIsGroup
                  ? 'Group Cheki'
                  : item.memberId
                    ? (members.find((m) => m.id === item.memberId)?.name ?? '—')
                    : 'Cheki'
                return (
                  <div key={item.localId} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className={effectiveIsGroup ? 'text-purple-700 font-medium' : 'font-medium'}>{label}</span>
                    <div className="flex items-center gap-4 text-right">
                      <span className="text-xs text-muted-foreground tabular-nums">{item.qty}× {formatCurrency(unitPrice)}</span>
                      <span className="font-semibold tabular-nums w-24">{formatCurrency(unitPrice * item.qty)}</span>
                    </div>
                  </div>
                )
              })}
            </div>
            <div className="flex items-center justify-between border-t border-border bg-muted/30 px-3 py-2">
              <span className="text-xs text-muted-foreground">{cartQty} cheki</span>
              <span className="text-sm font-bold tabular-nums">{formatCurrency(cartTotal)}</span>
            </div>
          </div>
        )}

        {/* Submit */}
        <div className="flex items-center justify-end gap-3">
          {createOrder.isError && (
            <p className="text-xs text-destructive flex-1">
              Gagal menyimpan: {(createOrder.error as Error).message}
            </p>
          )}
          <button
            type="submit"
            disabled={createOrder.isPending || cartItems.length === 0}
            className={`flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50 ${
              success
                ? 'bg-green-500 text-white'
                : 'bg-primary text-primary-foreground hover:bg-primary/90'
            }`}
          >
            <ShoppingCart className="h-4 w-4" />
            {submitLabel}
          </button>
        </div>
      </form>
    </div>
  )
}

// ── Session Summary ────────────────────────────────────────────────────────────

function SessionSummary({ otsOrders, form }: { otsOrders: ChekiOrder[]; form: ChekiForm }) {
  const [showRevenue, setShowRevenue] = useState(false)

  const totalQty = otsOrders.reduce((s, o) => s + (o.quantity ?? 1), 0)
  const totalRevenue = otsOrders.reduce((s, o) => {
    const price = o.is_group_cheki
      ? (form.group_price_ots_idr ?? form.group_price_idr ?? form.price_idr)
      : (form.price_ots_idr ?? form.price_idr)
    return s + price * (o.quantity ?? 1)
  }, 0)

  const groups = groupOTSOrders(otsOrders, form)
  const submissionCount = groups.length
  const byMethod = {
    cash: groups.filter((g) => g.paymentMethod === 'cash').length,
    qris: groups.filter((g) => g.paymentMethod === 'qris').length,
    transfer: groups.filter((g) => g.paymentMethod === 'transfer').length,
  }

  if (otsOrders.length === 0) return null

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {[
        { label: 'Pembelian OTS', value: String(submissionCount), isRevenue: false },
        { label: 'Total Cheki', value: String(totalQty), isRevenue: false },
        { label: 'Pendapatan OTS', value: formatCurrency(totalRevenue), isRevenue: true },
        {
          label: 'Cash / QRIS / TF',
          value: `${byMethod.cash} / ${byMethod.qris} / ${byMethod.transfer}`,
          isRevenue: false,
        },
      ].map(({ label, value, isRevenue }) => (
        <div key={label} className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs text-muted-foreground">{label}</p>
          {isRevenue ? (
            <div className="mt-1 flex items-center gap-2">
              <p className="text-xl font-bold tabular-nums">
                {showRevenue ? value : '••••••'}
              </p>
              <button
                onClick={() => setShowRevenue((v) => !v)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                {showRevenue ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </button>
            </div>
          ) : (
            <p className="mt-1 text-xl font-bold tabular-nums">{value}</p>
          )}
        </div>
      ))}
    </div>
  )
}

// ── Transaction Grouping ───────────────────────────────────────────────────────

interface TransactionGroup {
  groupId: string
  orders: ChekiOrder[]
  fanName: string
  fanPhone: string
  paymentMethod: ChekiOrder['payment_method']
  createdAt: string
  totalQty: number
  totalPrice: number
}

function groupOTSOrders(orders: ChekiOrder[], form: ChekiForm): TransactionGroup[] {
  const map = new Map<string, ChekiOrder[]>()
  for (const order of orders) {
    const gid = (order.custom_data?.transaction_group_id as string | undefined) ?? order.id
    const existing = map.get(gid) ?? []
    map.set(gid, [...existing, order])
  }
  return [...map.entries()].map(([groupId, grpOrders]) => ({
    groupId,
    orders: grpOrders,
    fanName: grpOrders[0].fan_name,
    fanPhone: grpOrders[0].fan_phone,
    paymentMethod: grpOrders[0].payment_method,
    createdAt: grpOrders[0].created_at,
    totalQty: grpOrders.reduce((s, o) => s + (o.quantity ?? 1), 0),
    totalPrice: grpOrders.reduce((s, o) => {
      const price = o.is_group_cheki
        ? (form.group_price_ots_idr ?? form.group_price_idr ?? form.price_idr)
        : (form.price_ots_idr ?? form.price_idr)
      return s + price * (o.quantity ?? 1)
    }, 0),
  }))
}

function paymentLabel(method: ChekiOrder['payment_method']): string {
  return method === 'cash' ? 'Cash' : method === 'qris' ? 'QRIS' : method === 'transfer' ? 'Transfer' : '—'
}

function paymentBadgeClass(method: ChekiOrder['payment_method']): string {
  return method === 'cash'
    ? 'bg-green-100 text-green-700'
    : method === 'qris'
      ? 'bg-blue-100 text-blue-700'
      : 'bg-orange-100 text-orange-700'
}

// ── Transactions Table ─────────────────────────────────────────────────────────

function OTSTransactionList({ otsOrders, form }: { otsOrders: ChekiOrder[]; form: ChekiForm }) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())

  if (otsOrders.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card p-8 text-center">
        <ShoppingCart className="mx-auto h-8 w-8 text-muted-foreground/40 mb-2" />
        <p className="text-sm text-muted-foreground">Belum ada transaksi OTS dicatat.</p>
      </div>
    )
  }

  const groups = groupOTSOrders(otsOrders, form)

  function toggleGroup(groupId: string) {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(groupId)) next.delete(groupId)
      else next.add(groupId)
      return next
    })
  }

  return (
    <div className="space-y-2">
      {groups.map((group) => {
        const isMulti = group.orders.length > 1
        const isExpanded = expandedGroups.has(group.groupId)
        const fanDisplay = group.fanName && group.fanName !== 'OTS' ? group.fanName : null

        return (
          <div key={group.groupId} className="rounded-xl border border-border overflow-hidden">
            {/* Group header */}
            <div
              className={`flex items-center gap-3 px-4 py-2.5 bg-card ${isMulti ? 'cursor-pointer hover:bg-muted/30 transition-colors' : ''}`}
              onClick={isMulti ? () => toggleGroup(group.groupId) : undefined}
            >
              {/* Toggle icon */}
              <div className="w-4 shrink-0">
                {isMulti && (
                  isExpanded
                    ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    : <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
              </div>

              {/* Fan info */}
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium">
                  {fanDisplay ?? <span className="text-muted-foreground text-xs">Tanpa nama</span>}
                </span>
                {group.fanPhone && (
                  <span className="ml-2 text-xs text-muted-foreground">{group.fanPhone}</span>
                )}
              </div>

              {/* Payment badge */}
              <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium shrink-0 ${paymentBadgeClass(group.paymentMethod)}`}>
                {paymentLabel(group.paymentMethod)}
              </span>

              {/* Qty + price */}
              <span className="text-xs text-muted-foreground shrink-0 tabular-nums">
                {group.orders.length > 1 && `${group.orders.length} item · `}{group.totalQty} cheki
              </span>
              <span className="text-sm font-semibold tabular-nums shrink-0">
                {formatCurrency(group.totalPrice)}
              </span>

              {/* Time */}
              <span className="text-xs text-muted-foreground flex items-center gap-1 shrink-0 whitespace-nowrap">
                <Clock className="h-3 w-3" />
                {formatDate(group.createdAt, { timeStyle: 'short', dateStyle: undefined })}
              </span>
            </div>

            {/* Child rows — shown when expanded (multi) or always (single) */}
            {(!isMulti || isExpanded) && (
              <div className="border-t border-border divide-y divide-border bg-muted/10">
                {group.orders.map((order) => {
                  const memberName = order.is_group_cheki
                    ? null
                    : (order.idol_members?.stage_name ?? order.idol_members?.name ?? '—')
                  return (
                    <div key={order.id} className="flex items-center gap-3 px-4 py-2 text-xs">
                      {/* indent for multi */}
                      {isMulti && <span className="text-muted-foreground/40 shrink-0">└</span>}
                      <span className="font-mono text-muted-foreground shrink-0">{order.booking_code}</span>
                      <span className="flex-1 min-w-0">
                        {order.is_group_cheki ? (
                          <span className="rounded-full bg-purple-100 px-1.5 py-0.5 text-xs font-medium text-purple-700">
                            Group Cheki
                          </span>
                        ) : (
                          <span className="text-muted-foreground">{memberName}</span>
                        )}
                      </span>
                      <span className="tabular-nums shrink-0">{order.quantity ?? 1}×</span>
                      <span className="tabular-nums font-medium shrink-0">
                        {formatCurrency(
                          (order.is_group_cheki
                            ? (form.group_price_ots_idr ?? form.group_price_idr ?? form.price_idr)
                            : (form.price_ots_idr ?? form.price_idr)) * (order.quantity ?? 1)
                        )}
                      </span>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── Main Panel ─────────────────────────────────────────────────────────────────

interface KasirOTSPanelProps {
  form: ChekiForm
  formId: string
  orders: ChekiOrder[]
  hasGroupCheki: boolean
}

export default function KasirOTSPanel({ form, formId, orders, hasGroupCheki }: KasirOTSPanelProps) {
  const { data: session, isLoading } = useKasirOTSSession(formId)
  const openSession = useOpenKasirSession()
  const closeSession = useCloseKasirSession()

  const otsOrders = orders.filter((o) => o.is_ots)
  const isPending = openSession.isPending || closeSession.isPending

  if (isLoading) {
    return <p className="py-8 text-center text-sm text-muted-foreground">Memuat sesi kasir...</p>
  }

  const sessionStatus = session?.status ?? null
  const isOpen = sessionStatus === 'open'

  return (
    <div className="space-y-4">
      {/* Session control */}
      <SessionHeader
        status={sessionStatus}
        onOpen={() => openSession.mutate(formId)}
        onClose={(sessionId, fid) => closeSession.mutate({ sessionId, formId: fid })}
        sessionId={session?.id ?? null}
        formId={formId}
        isPending={isPending}
      />

      {/* Summary stats (all OTS orders) */}
      <SessionSummary otsOrders={otsOrders} form={form} />

      {/* Transaction input form — only when open */}
      {isOpen && session && (
        <TransactionForm
          form={form}
          sessionId={session.id}
          hasGroupCheki={hasGroupCheki}
        />
      )}

      {/* OTS order history */}
      {(otsOrders.length > 0 || isOpen) && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-muted-foreground">
            Riwayat Transaksi OTS ({groupOTSOrders(otsOrders, form).length} pembelian)
          </h3>
          <OTSTransactionList otsOrders={otsOrders} form={form} />
        </div>
      )}

      {!isOpen && otsOrders.length === 0 && (
        <div className="rounded-xl border border-dashed border-border p-10 text-center">
          <ShoppingCart className="mx-auto h-10 w-10 text-muted-foreground/30 mb-3" />
          <p className="text-sm text-muted-foreground">
            Buka sesi kasir untuk mulai mencatat transaksi OTS.
          </p>
        </div>
      )}
    </div>
  )
}
